import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { toast } from "sonner";
import MarkdownLite from "@/components/MarkdownLite";
import {
  Loader2, Sparkles, Search, Check, X, Trash2, Send, ChevronDown, ChevronRight,
  ExternalLink, CalendarDays, PencilLine, Share2, Copy, Image as ImageIcon,
  BarChart3, Eye, UserPlus, Clock, Link2, RefreshCw, AlertCircle,
  PenLine, Settings, ListChecks, MessageSquarePlus, FileText, Wand2, Archive, Clapperboard, Sprout, FolderOpen,
} from "lucide-react";
import DirectorChat from "@/components/DirectorChat";
import ReelsAdmin from "@/components/ReelsAdmin";
import NuggetsAdmin from "@/components/NuggetsAdmin";
import MediaLibrary from "@/components/MediaLibrary";
import PublishedPosts from "@/components/PublishedPosts";
import { useHub, olderThan, matchesSearch } from "@/context/contentHub";

const COMPOSE_PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "tiktok", label: "TikTok" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  draft: "bg-amber-500/10 text-amber-300",
  scheduled: "bg-[#10222A] text-[#00E5FF]",
  published: "bg-green-500/10 text-green-300",
  rejected: "bg-[#141B29] text-slate-500 line-through",
};

const Q_STATUS_STYLE = {
  queued: "bg-amber-500/10 text-amber-300",
  dry_run: "bg-[#10222A] text-[#00E5FF]",
  submitted: "bg-green-500/10 text-green-300",
  published: "bg-green-500/10 text-green-300",
  failed: "bg-red-500/10 text-red-400",
};

const PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "tiktok", label: "TikTok" },
];

const PLATFORM_SHAPE = { instagram: "square", facebook: "landscape", linkedin: "landscape", tiktok: "portrait" };
const PLATFORM_LABEL = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.label]));

const QUEUE_TABS = [
  { key: "blog", label: "Blog", match: (q) => !!q.blog },
  { key: "facebook", label: "Facebook", match: (q) => !!q.fb_direct },
  { key: "instagram", label: "Instagram", match: (q) => (q.platforms || []).includes("instagram") },
  { key: "tiktok", label: "TikTok", match: (q) => (q.platforms || []).includes("tiktok") },
  { key: "linkedin", label: "LinkedIn", match: (q) => (q.platforms || []).includes("linkedin") },
  { key: "youtube", label: "YouTube", match: () => false },
];

const today = () => new Date().toISOString().slice(0, 10);
const copy = (t) => { navigator.clipboard?.writeText(t || ""); toast.success("Copied"); };
const toUtcIso = (localVal) => (localVal ? new Date(localVal).toISOString() : "");
const fmtWhen = (v) => {
  if (!v) return "as soon as approved";
  try {
    const d = new Date(v.length === 10 ? v + "T09:00:00Z" : v);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch { return v; }
};

// Collapsible config block used in the Setup tab to tuck Reels & Nuggets
// configuration out of the main creation surface without losing any capability.
function ConfigSection({ id, title, subtitle, icon: Icon, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`config-section-${id}`} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] overflow-hidden">
      <button data-testid={`config-toggle-${id}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#0B0F16]/60 transition-colors">
        <span className="w-9 h-9 rounded-xl bg-[#10222A] text-[#00E5FF] flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.7} />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-200">{title}</span>
          <span className="block text-xs text-slate-500">{subtitle}</span>
        </span>
        <ChevronDown className={`ml-auto w-4 h-4 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="border-t border-[#1E273D] p-4">{children}</div>}
    </div>
  );
}

function DeliveryChip({ tone, label, testid }) {
  const styles = {
    ok: "bg-green-500/10 text-green-300 border-green-500/30",
    warn: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    fail: "bg-red-500/10 text-red-400 border-red-500/30",
    idle: "bg-[#0B0F16] text-slate-400 border-[#29344D]",
  };
  return <span data-testid={testid} className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${styles[tone] || styles.idle}`}>{label}</span>;
}

// Post Status Trail — reads the stored publish response to show, per destination,
// whether Facebook / Blog / PostPulse actually received the post.
function DeliveryTrail({ q }) {
  const resp = q.postpulse_response || {};
  const acted = !!q.postpulse_response || ["submitted", "posted"].includes(q.status);
  if (!acted) return null;
  const chips = [];
  if (q.fb_direct) {
    const fb = resp.fb_direct;
    if (fb?.post_id) chips.push({ k: "fb", tone: "ok", label: "Facebook · posted" });
    else if (fb?.dry_run) chips.push({ k: "fb", tone: "warn", label: "Facebook · skipped" });
    else if (q.status === "failed") chips.push({ k: "fb", tone: "fail", label: "Facebook · failed" });
    else chips.push({ k: "fb", tone: "idle", label: "Facebook · pending" });
  }
  if (q.blog) {
    const b = resp.blog;
    if (b?.published) chips.push({ k: "blog", tone: "ok", label: b.already ? "Blog · already live" : "Blog · published" });
    else if (q.status === "failed") chips.push({ k: "blog", tone: "fail", label: "Blog · failed" });
    else chips.push({ k: "blog", tone: "idle", label: "Blog · pending" });
  }
  if ((q.platforms || []).length) {
    const pp = resp.postpulse;
    const plats = (q.platforms || []).join(", ");
    if (pp?.scheduled) chips.push({ k: "pp", tone: "ok", label: `PostPulse · sent (${plats})` });
    else if (pp?.dry_run) chips.push({ k: "pp", tone: "warn", label: `PostPulse · dry-run (${plats})` });
    else if (q.status === "failed") chips.push({ k: "pp", tone: "fail", label: "PostPulse · failed" });
    else chips.push({ k: "pp", tone: "idle", label: `PostPulse · pending (${plats})` });
  }
  if (!chips.length) return null;
  return (
    <div data-testid={`queue-trail-${q.id}`} className="flex flex-wrap items-center gap-1.5 mt-1.5">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">Sent to</span>
      {chips.map((c) => <DeliveryChip key={c.k} tone={c.tone} label={c.label} testid={`trail-${c.k}-${q.id}`} />)}
    </div>
  );
}

export default function ContentEngine({ view, onNavigate: setView }) {
  // topic research
  const [seed, setSeed] = useState("");
  const [count, setCount] = useState(5);
  const [researching, setResearching] = useState(false);
  const [grounded, setGrounded] = useState("");
  const [sources, setSources] = useState([]);
  const [topics, setTopics] = useState([]); // {title, angle, selected}
  // manual "write your own topic" draft
  const [manualTitle, setManualTitle] = useState("");
  const [manualAngle, setManualAngle] = useState("");
  const [draftingManual, setDraftingManual] = useState(false);
  // generation
  const [startDate, setStartDate] = useState(today());
  const [interval, setInterval] = useState(7);
  const [generating, setGenerating] = useState(false);
  // review
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [busy, setBusy] = useState("");
  const [queueOpen, setQueueOpen] = useState("");
  const [socialBusy, setSocialBusy] = useState("");
  const [openId, setOpenId] = useState("");
  const [draftEdit, setDraftEdit] = useState({});
  const [platformSel, setPlatformSel] = useState({}); // postId -> {platform: bool}
  const [quoteBust, setQuoteBust] = useState({}); // postId -> ts
  const [imgPrompt, setImgPrompt] = useState({}); // postId -> edited image prompt
  const [imgBusy, setImgBusy] = useState(""); // postId currently rerolling its photo
  const [scheduleAt, setScheduleAt] = useState({}); // postId -> local datetime string
  const [rebook, setRebook] = useState({}); // queueId -> local datetime string
  const [failDetail, setFailDetail] = useState({}); // queueId -> PostPulse failure reason
  const [failBusy, setFailBusy] = useState("");
  const [capEdit, setCapEdit] = useState({}); // `${qid}::${plat}` -> edited caption text
  const [capBusy, setCapBusy] = useState("");
  // social queue + analytics
  const [queue, setQueue] = useState([]);
  const [queueLoaded, setQueueLoaded] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [pp, setPp] = useState(null);
  const [ppBusy, setPpBusy] = useState(false);
  // dashboard view + create mode
  const [calOpen, setCalOpen] = useState(true);   // Content calendar collapsible
  const [sqOpen, setSqOpen] = useState(true);      // Social queue collapsible
  const [showArchive, setShowArchive] = useState(false);
  const { search: hubSearch = "", filter: hubFilter = "all", archiveDays = 7, reportSummary } = useHub();
  const [postMode, setPostMode] = useState("blog"); // blog | standalone
  const [queueTab, setQueueTab] = useState("blog"); // platform filter tab for the review queue
  // standalone social composer
  const [cPlatforms, setCPlatforms] = useState({});
  const [cFbDirect, setCFbDirect] = useState(true);
  const [cBlog, setCBlog] = useState(true);
  const [cMode, setCMode] = useState("write"); // write (own caption) | ai (prompt)
  const [cCaption, setCCaption] = useState("");
  const [cPrompt, setCPrompt] = useState("");
  const [cImagePrompt, setCImagePrompt] = useState("");
  const [cWhen, setCWhen] = useState("");
  const [composing, setComposing] = useState(false);
  // manual reel + nugget drafts (reuse the existing admin pipelines; review in Setup)
  const [reelBrief, setReelBrief] = useState("");
  const [reelPlatform, setReelPlatform] = useState("facebook");
  const [reelDrafting, setReelDrafting] = useState(false);
  const [nuggetDays, setNuggetDays] = useState(7);
  const [nuggetDrafting, setNuggetDrafting] = useState(false);

  const loadPosts = async () => {
    setLoadingPosts(true);
    try {
      const { data } = await api.get("/admin/content/posts");
      setPosts(data.posts || []);
    } catch (e) {
      toast.error("Couldn't load content");
    }
    setLoadingPosts(false);
  };
  const loadQueue = async () => {
    try {
      const { data } = await api.get("/admin/social/queue");
      setQueue(data.queue || []);
      setLiveMode(!!data.live_mode);
    } catch (e) { /* silent */ }
    finally { setQueueLoaded(true); }
  };
  const [refreshingQueue, setRefreshingQueue] = useState(false);
  const refreshStatuses = async () => {
    setRefreshingQueue(true);
    try {
      const { data } = await api.post("/admin/social/queue/refresh");
      setQueue(data.queue || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't refresh statuses — please try again");
    } finally { setRefreshingQueue(false); }
  };
  const draftReel = async () => {
    if (!reelBrief.trim()) { toast.error("Give a brief or theme for the reel"); return; }
    setReelDrafting(true);
    try {
      await api.post("/admin/reels/draft", { brief: reelBrief.trim(), platforms: [reelPlatform] });
      toast.success("Reel drafted — review, render & post it in Setup → Reels");
      setReelBrief("");
      setView("setup");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft the reel");
    }
    setReelDrafting(false);
  };
  const draftNuggets = async () => {
    setNuggetDrafting(true);
    try {
      const days = Math.max(1, Math.min(14, Number(nuggetDays) || 7));
      const { data } = await api.post(`/admin/nuggets/draft?days=${days}`);
      const n = (data.created?.length ?? data.count ?? 0);
      if (n > 0) toast.success(`Drafted ${n} daily nugget(s) — review them in Setup → Daily Nuggets`);
      else toast.info(`Your next ${days} day(s) already have nuggets drafted — nothing new to add. Review them in Setup → Daily Nuggets.`);
      setView("setup");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft nuggets");
    }
    setNuggetDrafting(false);
  };
  const loadAnalytics = async () => {
    try {
      const { data } = await api.get("/admin/content/analytics");
      setAnalytics(data);
    } catch (e) { /* silent */ }
  };
  const loadPp = async () => {
    try {
      const { data } = await api.get("/admin/social/postpulse/status");
      setPp(data);
      setLiveMode(!!data.connected);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    loadPosts(); loadQueue(); loadAnalytics(); loadPp();
    const p = new URLSearchParams(window.location.search).get("postpulse");
    if (p) {
      if (p === "connected") toast.success("PostPulse connected");
      else toast.error("PostPulse connection didn't complete. Please try again.");
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const connectPp = async () => {
    setPpBusy(true);
    try {
      const { data } = await api.get("/admin/social/postpulse/connect");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start PostPulse connect");
      setPpBusy(false);
    }
  };

  const refreshPpAccounts = async () => {
    setPpBusy(true);
    try {
      const { data } = await api.post("/admin/social/postpulse/refresh-accounts");
      setPp((s) => ({ ...(s || {}), platforms: data.platforms, accounts: data.accounts, account_count: data.account_count }));
      const missing = (data.accounts || []).filter((a) => a.needs_page);
      if (missing.length) {
        toast.warning(`${missing.map((a) => a.platform).join(", ")}: no Page selected in PostPulse yet — pick your Page there, then refresh again.`);
      } else {
        toast.success(`Found ${data.account_count} connected account(s)`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't refresh accounts");
    }
    setPpBusy(false);
  };

  const sendPpTestPost = async () => {
    setPpBusy(true);
    try {
      const { data } = await api.post("/admin/social/postpulse/test-post");
      toast.success(`Test post scheduled to account ${data.account_id} — go back to the PostPulse wizard, it should unlock Finish.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test post failed");
    }
    setPpBusy(false);
  };

  const research = async () => {
    setResearching(true);
    setTopics([]);
    try {
      const { data } = await api.post("/admin/content/themes", { seed, count: Number(count) });
      setGrounded(data.grounded_summary || "");
      setSources(data.sources || []);
      setTopics((data.topics || []).map((t) => ({ ...t, selected: true })));
      if (!(data.topics || []).length) toast.info("No topics returned — try a different focus.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Research failed");
    }
    setResearching(false);
  };

  const generate = async () => {
    const chosen = topics.filter((t) => t.selected).map(({ title, angle }) => ({ title, angle }));
    if (!chosen.length) return toast.error("Select at least one topic");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/content/generate", {
        topics: chosen, start_date: startDate, interval_days: Number(interval),
      });
      toast.success(`Generated ${data.posts.length} draft(s)`);
      setTopics([]); setGrounded(""); setSources([]);
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    }
    setGenerating(false);
  };

  const draftManual = async () => {
    const title = manualTitle.trim();
    if (title.length < 3) return toast.error("Give the topic a title (3+ characters)");
    setDraftingManual(true);
    try {
      const { data } = await api.post("/admin/content/generate", {
        topics: [{ title, angle: manualAngle.trim() }],
        start_date: startDate, interval_days: Number(interval),
      });
      toast.success(`Drafted ${data.posts.length} post`);
      setManualTitle(""); setManualAngle("");
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Draft failed");
    }
    setDraftingManual(false);
  };

  const patchPost = async (id, patch, okMsg) => {
    setBusy(id);
    try {
      const { data } = await api.put(`/admin/content/posts/${id}`, patch);
      setPosts((prev) => prev.map((p) => (p.id === id ? data : p)));
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
    setBusy("");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/content/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
    setBusy("");
  };

  const genSocial = async (p) => {
    setSocialBusy(p.id);
    try {
      const { data } = await api.post(`/admin/content/posts/${p.id}/social`);
      setPosts((prev) => prev.map((x) => (x.id === p.id ? { ...x, social_posts: data.social_posts, quote: data.quote, has_photo: data.has_photo, image_alt: data.image_alt } : x)));
      setQuoteBust((q) => ({ ...q, [p.id]: Date.now() }));
      toast.success(data.has_photo ? "Social posts + photo generated" : "Social posts generated (using the quote card — photo was skipped)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    }
    setSocialBusy("");
  };

  const regenPhoto = async (p) => {
    setImgBusy(p.id);
    try {
      const edited = imgPrompt[p.id];
      const body = edited != null && edited.trim() ? { prompt: edited.trim() } : {};
      const { data } = await api.post(`/admin/content/posts/${p.id}/image`, body);
      setPosts((prev) => prev.map((x) => (x.id === p.id
        ? { ...x, has_photo: data.has_photo, image_prompt: data.image_prompt, image_alt: data.image_alt, image_shapes: data.image_shapes }
        : x)));
      setImgPrompt((s) => ({ ...s, [p.id]: data.image_prompt }));
      setQuoteBust((q) => ({ ...q, [p.id]: Date.now() }));
      toast.success(data.has_photo ? "New photo generated" : "Couldn't generate a photo — using the quote card");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't regenerate the photo");
    }
    setImgBusy("");
  };

  const addToQueue = async (p) => {
    const sel = platformSel[p.id] || {};
    const chosen = PLATFORMS.filter((pl) => (sel[pl.key] ?? (pl.key === "facebook")) && p.social_posts?.[pl.key]).map((pl) => pl.key);
    if (!chosen.length) return toast.error("No platforms with a caption selected");
    setSocialBusy(p.id);
    try {
      await api.post("/admin/social/queue", { post_id: p.id, platforms: chosen, fb_direct: true, blog: true, scheduled_time: toUtcIso(scheduleAt[p.id]) });
      toast.success(scheduleAt[p.id] ? "Queued & scheduled" : "Added to social queue");
      loadQueue();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't queue");
    }
    setSocialBusy("");
  };

  const rescheduleQueue = async (q) => {
    const val = rebook[q.id];
    if (!val) return;
    setBusy(q.id);
    try {
      const { data } = await api.patch(`/admin/social/queue/${q.id}`, { scheduled_time: toUtcIso(val) });
      setQueue((prev) => prev.map((x) => (x.id === q.id ? data : x)));
      setRebook((r) => ({ ...r, [q.id]: "" }));
      toast.success("Rescheduled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reschedule");
    }
    setBusy("");
  };

  const saveCaptionEdit = async (q, plat) => {
    const key = `${q.id}::${plat}`;
    const text = capEdit[key];
    if (text == null) return;
    setCapBusy(key);
    try {
      const { data } = await api.patch(`/admin/social/queue/${q.id}`, { captions: { [plat]: text } });
      setQueue((prev) => prev.map((x) => (x.id === q.id ? data : x)));
      setCapEdit((e) => { const n = { ...e }; delete n[key]; return n; });
      toast.success("Caption updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the caption");
    }
    setCapBusy("");
  };

  const approveQueue = async (q, immediate = false) => {
    setBusy(q.id);
    try {
      const { data } = await api.post(`/admin/social/queue/${q.id}/approve`, { immediate });
      setQueue((prev) => prev.map((x) => (x.id === q.id ? data : x)));
      const fallback = data.status === "submitted"
        ? (immediate ? "Posting now" : "Scheduled")
        : "Approved (dry-run — connect a destination to go live)";
      toast.success(data.status_detail || fallback);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    }
    setBusy("");
  };

  // Reject / Edit a staged post by routing it BACK into Shee's chat so she can revise it.
  // The item is pulled out of the queue immediately; its copy seeds the composer.
  const [reviseSeed, setReviseSeed] = useState("");
  const buildRevisionText = (q, mode) => {
    const parts = [mode === "reject"
      ? "I've rejected this staged post — please rework it."
      : "Let's edit this staged post."];
    if (q.title) parts.push(`\nTitle: ${q.title}`);
    const caps = q.captions || {};
    const capText = Object.entries(caps).map(([p, t]) => `[${p}]\n${t}`).join("\n\n");
    if (capText) parts.push(`\nCurrent copy:\n${capText}`);
    if (q.kind === "nugget" && q.nugget) {
      const n = q.nugget;
      parts.push(`\nNugget:\n"${n.verse || ""}" — ${n.reference || ""}\n${n.catch || ""}\n${n.question || ""}`);
    }
    if (q.quote) parts.push(`\nQuote: "${q.quote}"`);
    parts.push(mode === "reject" ? "\n\nWhat's off: " : "\n\nWhat I'd like changed: ");
    return parts.join("\n");
  };
  const sendToShee = async (q, mode) => {
    setBusy(q.id);
    try {
      await api.delete(`/admin/social/queue/${q.id}`);
      setQueue((prev) => prev.filter((x) => x.id !== q.id));
      setReviseSeed(buildRevisionText(q, mode));
      setView("chat");
      toast.success(mode === "reject" ? "Removed — sent back to Shee to rework" : "Sent to Shee to edit");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send to Shee");
    }
    setBusy("");
  };


  // Edit destinations / format on a queued item (optimistic PATCH).
  const patchQueue = async (q, patch) => {
    const prev = queue;
    setQueue((s) => s.map((x) => (x.id === q.id ? { ...x, ...patch } : x)));
    try {
      const { data } = await api.patch(`/admin/social/queue/${q.id}`, patch);
      setQueue((s) => s.map((x) => (x.id === q.id ? data : x)));
    } catch (e) {
      setQueue(prev);
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
  };
  const toggleDest = (q, key) => {
    if (key === "fb_direct") patchQueue(q, { fb_direct: !q.fb_direct });
    else if (key === "blog") patchQueue(q, { blog: !q.blog });
  };
  const togglePP = (q, plat) => {
    const cur = new Set(q.platforms || []);
    if (cur.has(plat)) cur.delete(plat); else cur.add(plat);
    patchQueue(q, { platforms: Array.from(cur) });
  };
  const setFormat = (q, textOnly) => patchQueue(q, { text_only: textOnly });

  const repostQueue = async (q, fbDirect = false) => {
    setBusy(q.id);
    try {
      const { data } = await api.post(`/admin/social/queue/${q.id}/repost`, { immediate: true, fb_direct: fbDirect });
      setQueue((prev) => prev.map((x) => (x.id === q.id ? { ...x, ...data } : x)));
      toast.success(data.status_detail || (fbDirect ? "Posting directly now" : "Re-posting now"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Re-post failed");
    }
    setBusy("");
  };

  const reopenQueue = async (q) => {
    setBusy(q.id);
    try {
      const { data } = await api.post(`/admin/social/queue/${q.id}/repost`, { immediate: false });
      setQueue((prev) => prev.map((x) => (x.id === q.id ? { ...x, ...data } : x)));
      toast.success("Re-opened — choose a destination and post it");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't re-open");
    }
    setBusy("");
  };

  const delQueue = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/social/queue/${id}`);
      setQueue((prev) => prev.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error("Remove failed"); }
    setBusy("");
  };

  const showFailReason = async (q) => {
    setFailBusy(q.id);
    try {
      const { data } = await api.get(`/admin/social/queue/${q.id}/pp-detail`);
      const msg = (data.errors && data.errors.length) ? data.errors.join(" · ") : "";
      const full = `${data.overall_status || "?"}${msg ? " — " + msg : " (PostPulse returned no specific reason)"}`;
      setFailDetail((d) => ({ ...d, [q.id]: full }));
      toast.info(`PostPulse: ${full}`, { duration: 14000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't fetch the reason");
    }
    setFailBusy("");
  };

  const purgeBlogs = async () => {
    if (!window.confirm("Remove ALL blog posts, images, and the social queue? This can't be undone.")) return;
    setPpBusy(true);
    try {
      const { data } = await api.post("/admin/content/purge-blogs");
      setPosts([]); setQueue([]);
      toast.success(`Cleared ${data.blog_posts} posts, ${data.queue} queued items`);
    } catch (e) {
      toast.error("Purge failed");
    }
    setPpBusy(false);
  };

  const composePost = async () => {
    const platforms = Object.keys(cPlatforms).filter((k) => cPlatforms[k]);
    if (!cFbDirect && !cBlog && platforms.length === 0) return toast.error("Pick at least one destination");
    if (cMode === "write" && !cCaption.trim()) return toast.error("Write your caption");
    if (cMode === "ai" && !cPrompt.trim()) return toast.error("Tell the AI what to post about");
    setComposing(true);
    try {
      const { data } = await api.post("/admin/social/compose", {
        mode: cMode === "ai" ? "ai" : "manual",
        platforms,
        fb_direct: cFbDirect,
        blog: cBlog,
        caption: cCaption.trim(),
        prompt: cPrompt.trim(),
        image_prompt: cImagePrompt.trim(),
        scheduled_time: toUtcIso(cWhen),
      });
      setQueue((prev) => [data, ...prev]);
      toast.success("Added to your Queue — tune destinations and post it there");
      setCCaption(""); setCPrompt(""); setCImagePrompt(""); setCWhen("");
      setView("queue");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the post");
    }
    setComposing(false);
  };

  const saveEdit = (p) => {
    const e = draftEdit[p.id] || {};
    patchPost(p.id, {
      title: e.title ?? p.title,
      body_markdown: e.body_markdown ?? p.body_markdown,
      scheduled_publish_date: e.scheduled_publish_date ?? p.scheduled_publish_date,
    }, "Saved");
  };

  const ed = (p, field) => (draftEdit[p.id]?.[field] ?? p[field] ?? "");
  const setEd = (id, field, val) => setDraftEdit((d) => ({ ...d, [id]: { ...d[id], [field]: val } }));

  useEffect(() => {
    if (!reportSummary || !queueLoaded) return;
    const c = {};
    queue.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    const dots = [];
    if (c.queued) dots.push({ label: `${c.queued} queued`, tone: "amber" });
    if (c.dry_run) dots.push({ label: `${c.dry_run} dry-run`, tone: "zinc" });
    if (c.failed) dots.push({ label: `${c.failed} failed`, tone: "red" });
    if (c.submitted) dots.push({ label: `${c.submitted} posted`, tone: "green" });
    const needsAction = (c.queued || 0) + (c.dry_run || 0) + (c.failed || 0);
    reportSummary({ subtitle: `${queue.length} total · ${needsAction} to review`, needsAction, dots });
  }, [queue, queueLoaded, reportSummary]);

  const NEEDS_Q = ["queued", "dry_run", "failed"];
  const qMatch = (q) =>
    matchesSearch(`${q.title || ""} ${Object.values(q.captions || {}).join(" ")}`, hubSearch) &&
    (hubFilter === "all" ||
      (hubFilter === "action" && NEEDS_Q.includes(q.status)) ||
      (hubFilter === "scheduled" && !!q.scheduled_time && q.status !== "submitted") ||
      (hubFilter === "posted" && q.status === "submitted"));
  const isArchQ = (q) => !NEEDS_Q.includes(q.status) && olderThan(q.scheduled_time || q.created_at, archiveDays);
  const shownQ = queue.filter(qMatch);
  const activePlatformMatch = QUEUE_TABS.find((t) => t.key === queueTab)?.match || (() => true);
  const platformQ = shownQ.filter(activePlatformMatch);
  const activeQ = platformQ.filter((q) => !isArchQ(q));
  const archivedQ = platformQ.filter(isArchQ);

  return (
    <div data-testid="admin-content" className="space-y-6">
      {/* STUDIO — Shee (Creative Director) */}
      {view === "chat" && (
        <DirectorChat seed={reviseSeed} onSeedConsumed={() => setReviseSeed("")}
                      onQueueChange={loadQueue} onGoToReview={() => setView("queue")} />
      )}

      {/* TRACK */}
      {view === "track" && (
      <section data-testid="content-analytics" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
          <BarChart3 className="w-4 h-4" strokeWidth={1.5} /> Blog analytics
        </div>
        {!analytics && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {analytics && (
          <>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-[#07090E] border border-[#1E273D] px-4 py-3">
                <Eye className="w-4 h-4 text-[#00E5FF]" />
                <div><div className="text-lg font-semibold text-slate-200" data-testid="analytics-total-views">{analytics.total_views}</div><div className="text-[11px] text-slate-500">total post views</div></div>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-[#07090E] border border-[#1E273D] px-4 py-3">
                <UserPlus className="w-4 h-4 text-[#00E5FF]" />
                <div><div className="text-lg font-semibold text-slate-200" data-testid="analytics-total-signups">{analytics.total_blog_signups}</div><div className="text-[11px] text-slate-500">signups from blog</div></div>
              </div>
            </div>
            {analytics.posts?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[420px]">
                  <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-[#1E273D]">
                    <th className="py-2 pr-3">Post</th><th className="py-2 px-3">Status</th><th className="py-2 px-3 text-right">Views</th><th className="py-2 pl-3 text-right">Signups</th>
                  </tr></thead>
                  <tbody>
                    {analytics.posts.map((r) => (
                      <tr key={r.slug} className="border-b border-[#1E273D]">
                        <td className="py-2 pr-3 text-slate-300 truncate max-w-[220px]">{r.title}</td>
                        <td className="py-2 px-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span></td>
                        <td className="py-2 px-3 text-right tabular-nums text-slate-400">{r.views}</td>
                        <td className="py-2 pl-3 text-right tabular-nums text-[#00E5FF] font-medium">{r.signups}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
      )}

      {/* CREATE (manual fallback composer) */}
      {view === "manual" && (
      <div className="space-y-4">
        <div className="flex items-center gap-2 pt-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <PenLine className="w-3.5 h-3.5" strokeWidth={1.5} /> Pick a type, draft it, then choose destinations
        </div>
        <div data-testid="create-mode-toggle" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { key: "blog", label: "Blog post", icon: FileText },
            { key: "standalone", label: "Social post", icon: MessageSquarePlus },
            { key: "reel", label: "Reel", icon: Clapperboard },
            { key: "nugget", label: "Daily Nugget", icon: Sprout },
          ].map((m) => (
            <button key={m.key} data-testid={`create-mode-${m.key}`} onClick={() => setPostMode(m.key)}
                    className={`h-11 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 ${postMode === m.key ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40"}`}>
              <m.icon className="w-4 h-4" strokeWidth={1.5} /> {m.label}
            </button>
          ))}
        </div>

        {postMode === "blog" && (
      <section className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
          <Search className="w-4 h-4" strokeWidth={1.5} /> Propose topics (live web research)
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="content-seed"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="Optional focus, e.g. loneliness, burnout, technology…"
            className="flex-1 h-11 px-4 rounded-xl border border-[#29344D] text-sm"
          />
          <input
            data-testid="content-count"
            type="number" min={1} max={8}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="w-20 h-11 px-3 rounded-xl border border-[#29344D] text-sm"
          />
          <button
            data-testid="content-research-btn"
            onClick={research}
            disabled={researching}
            className="h-11 px-5 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {researching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            Research
          </button>
        </div>

        {/* Or write your own topic — drafts directly, skipping research */}
        <div className="pt-2 border-t border-[#1E273D] space-y-2">
          <div className="text-xs text-slate-500">Or write your own topic — drafts directly, no research needed.</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="content-manual-title"
              value={manualTitle}
              onChange={(e) => setManualTitle(e.target.value)}
              placeholder="Exact title, e.g. Finding rest when the striving won't stop"
              className="flex-1 h-11 px-4 rounded-xl border border-[#29344D] text-sm"
            />
            <button
              data-testid="content-manual-draft-btn"
              onClick={draftManual}
              disabled={draftingManual}
              className="h-11 px-5 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {draftingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
              Draft this
            </button>
          </div>
          <input
            data-testid="content-manual-angle"
            value={manualAngle}
            onChange={(e) => setManualAngle(e.target.value)}
            placeholder="Optional angle / notes to steer the draft"
            className="w-full h-11 px-4 rounded-xl border border-[#29344D] text-sm"
          />
        </div>

        {grounded && (
          <details className="text-sm text-slate-400">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-slate-500">What the web is saying</summary>
            <div className="mt-2 whitespace-pre-wrap leading-relaxed">{grounded}</div>
            {sources.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer"
                     className="text-xs text-[#00E5FF] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> {s.title || new URL(s.url).hostname}
                  </a>
                ))}
              </div>
            )}
          </details>
        )}

        {topics.length > 0 && (
          <div data-testid="content-topics" className="space-y-2">
            {topics.map((t, i) => (
              <label key={i} className="flex items-start gap-3 p-3 rounded-xl border border-[#1E273D] hover:border-[#00E5FF]/30 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`content-topic-${i}`}
                  checked={t.selected}
                  onChange={() => setTopics((prev) => prev.map((x, j) => (j === i ? { ...x, selected: !x.selected } : x)))}
                  className="mt-1"
                />
                <div className="flex-1">
                  <input
                    value={t.title}
                    onChange={(e) => setTopics((prev) => prev.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                    className="w-full font-medium text-slate-200 bg-transparent focus:outline-none"
                  />
                  <div className="text-xs text-slate-400 mt-0.5">{t.angle}</div>
                </div>
              </label>
            ))}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Start
                <input data-testid="content-start-date" type="date" value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       className="h-9 px-2 rounded-lg border border-[#29344D] text-sm" />
              </label>
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Every
                <input data-testid="content-interval" type="number" min={1} value={interval}
                       onChange={(e) => setInterval(e.target.value)}
                       className="w-16 h-9 px-2 rounded-lg border border-[#29344D] text-sm" />
                days
              </label>
              <button
                data-testid="content-generate-btn"
                onClick={generate}
                disabled={generating}
                className="sm:ml-auto h-10 px-5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                Generate drafts
              </button>
            </div>
          </div>
        )}
      </section>
        )}

        {postMode === "standalone" && (
          <section data-testid="standalone-composer" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
              <MessageSquarePlus className="w-4 h-4" strokeWidth={1.5} /> New standalone post
            </div>
            <p className="text-sm text-slate-400">Post straight to Facebook and/or publish a companion blog in seconds. Write your own caption, or let AI draft it — either way it lands in your Queue where you can fine-tune destinations before posting.</p>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Destinations</div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="compose-dest-fb" onClick={() => setCFbDirect((v) => !v)}
                        className={`h-9 px-4 rounded-full border text-sm font-medium flex items-center gap-1.5 ${cFbDirect ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40"}`}>
                  {cFbDirect && <Check className="w-3.5 h-3.5" />} Facebook (Direct)
                </button>
                <button data-testid="compose-dest-blog" onClick={() => setCBlog((v) => !v)}
                        className={`h-9 px-4 rounded-full border text-sm font-medium flex items-center gap-1.5 ${cBlog ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40"}`}>
                  {cBlog && <Check className="w-3.5 h-3.5" />} Blog
                </button>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-3 mb-2">Also via PostPulse (optional)</div>
              <div className="flex flex-wrap gap-2">
                {COMPOSE_PLATFORMS.map((pl) => (
                  <button key={pl.key} data-testid={`compose-platform-${pl.key}`}
                          onClick={() => setCPlatforms((s) => ({ ...s, [pl.key]: !s[pl.key] }))}
                          className={`h-9 px-4 rounded-full border text-sm font-medium ${cPlatforms[pl.key] ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40"}`}>
                    {pl.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button data-testid="compose-mode-write" onClick={() => setCMode("write")}
                      className={`flex-1 h-10 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 ${cMode === "write" ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400"}`}>
                <PenLine className="w-4 h-4" /> Write my own
              </button>
              <button data-testid="compose-mode-ai" onClick={() => setCMode("ai")}
                      className={`flex-1 h-10 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 ${cMode === "ai" ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-400"}`}>
                <Sparkles className="w-4 h-4" /> Let AI write it
              </button>
            </div>

            {cMode === "write" ? (
              <textarea data-testid="compose-caption" value={cCaption} onChange={(e) => setCCaption(e.target.value)} rows={4}
                        placeholder="Write your caption. It’ll be used for every platform you selected."
                        className="w-full p-3 rounded-xl border border-[#29344D] text-sm leading-relaxed" />
            ) : (
              <textarea data-testid="compose-prompt" value={cPrompt} onChange={(e) => setCPrompt(e.target.value)} rows={3}
                        placeholder="What should this post be about? e.g. ‘encouragement for anyone carrying worry this week’"
                        className="w-full p-3 rounded-xl border border-[#29344D] text-sm leading-relaxed" />
            )}

            <input data-testid="compose-image-prompt" value={cImagePrompt} onChange={(e) => setCImagePrompt(e.target.value)}
                   placeholder="Optional: describe a real-life photo for the image (leave empty for a branded quote card)"
                   className="w-full h-11 px-4 rounded-xl border border-[#29344D] text-sm" />

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Schedule for (optional)
                <input type="datetime-local" data-testid="compose-when" value={cWhen} onChange={(e) => setCWhen(e.target.value)}
                       className="h-9 px-2 rounded-lg border border-[#29344D] text-sm" />
              </label>
              <button data-testid="compose-submit" onClick={composePost} disabled={composing}
                      className="sm:ml-auto h-11 px-6 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {composing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                Add to queue
              </button>
            </div>
          </section>
        )}

        {postMode === "reel" && (
          <section data-testid="reel-composer" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
              <Clapperboard className="w-4 h-4" strokeWidth={1.5} /> New reel
            </div>
            <p className="text-sm text-slate-400">Describe the reel and Shee drafts a script + video prompt. Review, render and post it from Setup &rarr; Reels (voice, avatar &amp; length come from your saved Reel settings there).</p>
            <textarea data-testid="reel-brief" value={reelBrief} onChange={(e) => setReelBrief(e.target.value)} rows={3}
                      placeholder="What's the reel about? e.g. &lsquo;the meaning of shalom &mdash; wholeness, not just peace&rsquo;"
                      className="w-full p-3 rounded-xl border border-[#29344D] text-sm leading-relaxed" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Platform
                <select data-testid="reel-platform" value={reelPlatform} onChange={(e) => setReelPlatform(e.target.value)}
                        className="h-9 px-2 rounded-lg border border-[#29344D] text-sm">
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                </select>
              </label>
              <button data-testid="reel-draft-btn" onClick={draftReel} disabled={reelDrafting}
                      className="sm:ml-auto h-11 px-6 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {reelDrafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                Draft reel
              </button>
            </div>
          </section>
        )}

        {postMode === "nugget" && (
          <section data-testid="nugget-composer" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
              <Sprout className="w-4 h-4" strokeWidth={1.5} /> Daily Nuggets
            </div>
            <p className="text-sm text-slate-400">Draft a run of short daily reflections ahead of time. Review, edit and approve each one in Setup &rarr; Daily Nuggets.</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Draft the next
                <input data-testid="nugget-days" type="number" min={1} max={14} value={nuggetDays}
                       onChange={(e) => setNuggetDays(e.target.value)}
                       className="w-16 h-9 px-2 rounded-lg border border-[#29344D] text-sm" />
                days
              </label>
              <button data-testid="nugget-draft-btn" onClick={draftNuggets} disabled={nuggetDrafting}
                      className="sm:ml-auto h-11 px-6 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {nuggetDrafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                Draft nuggets
              </button>
            </div>
          </section>
        )}
      </div>
      )}

      {/* REVIEW queue + SETUP (PostPulse) */}
      {(view === "queue" || view === "setup") && (
      <section data-testid="social-queue" className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-400">
          {view === "setup" ? (
            <><Settings className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Setup · PostPulse connection</>
          ) : (
            <button data-testid="sq-toggle" onClick={() => setSqOpen((v) => !v)} className="flex items-center gap-2 uppercase tracking-widest">
              <Share2 className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Social queue
              <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${sqOpen ? "" : "-rotate-90"}`} />
            </button>
          )}
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${liveMode ? "bg-green-500/10 text-green-300" : "bg-amber-500/10 text-amber-300"}`}>
            {liveMode ? "PostPulse connected" : "Dry-run (not connected)"}
          </span>
          {view === "queue" && (
            <button data-testid="queue-refresh-status" onClick={refreshStatuses} disabled={refreshingQueue}
                    className="ml-auto normal-case tracking-normal text-[11px] text-[#00E5FF] hover:underline flex items-center gap-1 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshingQueue ? "animate-spin" : ""}`} /> Refresh statuses
            </button>
          )}
        </div>
        {view === "queue" && (
          <div data-testid="queue-platform-tabs" className="flex items-center gap-1.5 overflow-x-auto pb-1">
            {QUEUE_TABS.map((t) => {
              const count = shownQ.filter(t.match).length;
              return (
                <button key={t.key} data-testid={`queue-tab-${t.key}`} onClick={() => setQueueTab(t.key)}
                        className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${queueTab === t.key ? "bg-[#00E5FF] text-[#07090E]" : "border border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40 hover:text-[#00E5FF]"}`}>
                  {t.label}
                  <span className={`text-[10px] ${queueTab === t.key ? "text-[#07090E]/70" : "text-slate-500"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        )}
        {(view === "setup" || sqOpen) && (<div className="space-y-3">

        {view === "setup" && (
        <div data-testid="postpulse-panel" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 space-y-3">
          {!pp && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking PostPulse…</div>}
          {pp && !pp.connected && (
            <>
              <p className="text-sm text-slate-400">
                {pp.has_credentials
                  ? "Connect your PostPulse account to post for real. Until then, approvals stay in safe dry-run."
                  : "PostPulse isn't configured yet — add your Client Secret to the backend, then connect. Approvals stay in dry-run for now."}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid="postpulse-connect"
                  onClick={connectPp}
                  disabled={ppBusy || !pp.has_credentials}
                  className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {ppBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" strokeWidth={1.5} />} Connect PostPulse
                </button>
              </div>
              <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                <span>Redirect URI to register in PostPulse:</span>
                <code className="bg-[#0B0F16] border border-[#1E273D] rounded px-1.5 py-0.5 text-slate-400">{pp.redirect_uri}</code>
                <button onClick={() => copy(pp.redirect_uri)} className="text-slate-500 hover:text-[#00E5FF]"><Copy className="w-3 h-3" /></button>
              </div>
            </>
          )}
          {pp && pp.connected && (
            <div className="space-y-3">
              {pp.needs_reconnect && (
                <div data-testid="postpulse-reconnect-warning" className="flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2.5 text-sm text-amber-300">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                  <span>Your PostPulse sign-in has expired, so posts can&rsquo;t be sent. Tap <strong>Reconnect</strong> to sign in again, then approve your post.</span>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-400">
                  <span className="inline-flex items-center gap-1.5 text-green-300 font-medium"><Check className="w-4 h-4" /> Connected</span>
                  <span className="text-slate-500"> · {pp.account_count || 0} account(s)</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="postpulse-reconnect"
                    onClick={connectPp}
                    disabled={ppBusy}
                    className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 ${pp.needs_reconnect ? "bg-amber-500/100 hover:bg-amber-600 text-white" : "border border-[#29344D] text-slate-400 hover:text-[#00E5FF] hover:border-[#00E5FF]/40"}`}
                  >
                    {ppBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} />} Reconnect
                  </button>
                  <button
                    data-testid="postpulse-refresh"
                    onClick={refreshPpAccounts}
                    disabled={ppBusy}
                    className="h-8 px-3 rounded-full border border-[#29344D] text-slate-400 hover:text-[#00E5FF] hover:border-[#00E5FF]/40 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {ppBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />} Refresh accounts
                  </button>
                  <button
                    data-testid="postpulse-test-post"
                    onClick={sendPpTestPost}
                    disabled={ppBusy}
                    className="h-8 px-3 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {ppBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.5} />} Send test post
                  </button>
                </div>
              </div>

              {/* Per-account: is a Page/channel linked? */}
              <div data-testid="postpulse-accounts" className="space-y-2">
                {(pp.accounts || []).length === 0 && (
                  <p className="text-sm text-slate-500">No accounts linked yet. Connect one in PostPulse, then Refresh accounts.</p>
                )}
                {(pp.accounts || []).map((a, i) => (
                  <div key={i} data-testid={`pp-account-${a.platform}`}
                       className={`rounded-xl border p-3 ${a.needs_page ? "border-amber-500/30 bg-amber-500/10" : "border-[#1E273D] bg-[#07090E]"}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-300 capitalize">{a.platform}</span>
                      {a.page ? (
                        <span data-testid={`pp-page-${a.platform}`} className="text-xs text-green-300 inline-flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" /> Page: {a.page.title}
                        </span>
                      ) : a.needs_page ? (
                        <span data-testid={`pp-nopage-${a.platform}`} className="text-xs text-amber-300 font-medium">⚠ No Page selected</span>
                      ) : (
                        <span className="text-xs text-slate-500">{a.name}</span>
                      )}
                    </div>
                    {a.needs_page && (
                      <p className="text-[11px] text-amber-300 mt-1 leading-relaxed">
                        {a.platform === "facebook" ? "Facebook" : "This platform"} posts must go to a Page. In PostPulse, connect the Page you want to post to (e.g. “OpenWord AI”), then tap “Refresh accounts” here. Until then, posts to this platform can’t be delivered.
                      </p>
                    )}
                    {a.needs_reauth && (
                      <p className="text-[11px] text-red-400 mt-1">This account needs to be re-authorised in PostPulse.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {view === "setup" && (
          <div data-testid="reels-nuggets-config" className="space-y-3">
            <ConfigSection id="media" title="Media Library" subtitle="Upload, import & manage photos/videos Shee can use" icon={FolderOpen}>
              <MediaLibrary />
            </ConfigSection>
            <ConfigSection id="reels" title="Reels" subtitle="Draft, render, voice, avatar & daily cap" icon={Clapperboard}>
              <ReelsAdmin />
            </ConfigSection>
            <ConfigSection id="nuggets" title="Daily Nuggets" subtitle="Draft ahead, schedule & subscribers" icon={Sprout}>
              <NuggetsAdmin />
            </ConfigSection>
            <ConfigSection id="published" title="Published Articles" subtitle="Unpublish or permanently delete a live blog post" icon={FileText}>
              <PublishedPosts />
            </ConfigSection>
          </div>
        )}

        {view === "setup" && (
          <div data-testid="content-danger-zone" className="rounded-2xl border border-red-500/20 bg-red-500/10 p-5">
            <div className="text-xs font-medium uppercase tracking-widest text-red-500 mb-1">Clean slate</div>
            <p className="text-sm text-slate-400 mb-3">Remove all blog posts, their images, and everything in the social queue. Use this once to clear old or broken content.</p>
            <button data-testid="purge-blogs-btn" onClick={purgeBlogs} disabled={ppBusy}
                    className="h-9 px-4 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/15 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
              {ppBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />} Remove all blog posts
            </button>
          </div>
        )}

        {view === "queue" && queueTab === "youtube" && (
          <p data-testid="queue-youtube-placeholder" className="text-sm text-slate-500">OpenWord AI doesn&rsquo;t publish to YouTube yet — this tab is a placeholder for when that&rsquo;s added.</p>
        )}
        {view === "queue" && queueTab !== "youtube" && platformQ.length === 0 && <p data-testid="queue-empty" className="text-sm text-slate-500">{queue.length === 0 ? "Nothing to review yet. Create a post in Chat with Shee or the Manual tab and it’ll appear here to set destinations and post." : "Nothing matches this tab, your search, or filter."}</p>}
        {view === "queue" && activeQ.map((q) => (
          <div key={q.id} data-testid={`queue-item-${q.id}`} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex gap-3">
                {q.slug && !q.text_only && (
                  <img
                    data-testid={`queue-image-${q.id}`}
                    src={`${BACKEND}/api/blog/${q.slug}/post-image.png`}
                    alt={q.image_alt || "Post image"}
                    title={q.has_photo ? "Real-life photo" : "Branded quote card (fallback)"}
                    className="w-16 h-16 rounded-lg object-cover border border-[#29344D] shrink-0" />
                )}
                <div className="min-w-0">
                <div className="font-medium text-slate-200 truncate">{q.title}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span data-testid={`queue-status-${q.id}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${Q_STATUS_STYLE[q.status] || ""}`}>{q.status === "dry_run" ? "dry-run" : q.status}</span>
                  {q.auto && <span data-testid={`queue-auto-${q.id}`} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#10222A] text-[#00E5FF]">auto</span>}
                  <span data-testid={`queue-time-${q.id}`} className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtWhen(q.scheduled_time)}</span>
                  <span data-testid={`queue-platforms-${q.id}`} className="text-xs text-slate-400">
                    {[q.fb_direct && "Facebook", q.blog && "Blog", ...(q.platforms || []).map((p) => `PP:${p}`)].filter(Boolean).join(" · ") || "no destination"}
                  </span>
                </div>
                {q.status_detail && (
                  <div data-testid={`queue-detail-${q.id}`}
                       className={`text-[11px] mt-1 leading-relaxed ${q.status === "failed" ? "text-red-400" : "text-slate-400"}`}>
                    {q.status_detail}
                  </div>
                )}
                <DeliveryTrail q={q} />
                {q.status === "failed" && (
                  <div className="mt-1">
                    <button data-testid={`queue-why-${q.id}`} onClick={() => showFailReason(q)} disabled={failBusy === q.id}
                            className="text-[11px] font-medium text-red-400 hover:text-red-300 inline-flex items-center gap-1 disabled:opacity-50">
                      {failBusy === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3" />} Why did it fail?
                    </button>
                    {failDetail[q.id] && (
                      <div data-testid={`queue-failreason-${q.id}`} className="text-[11px] mt-1 text-red-500 bg-red-500/10 rounded-lg px-2.5 py-1.5 leading-relaxed">
                        {failDetail[q.id]}
                      </div>
                    )}
                  </div>
                )}
                {["queued", "dry_run", "failed"].includes(q.status) && (
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <input
                      type="datetime-local"
                      data-testid={`reschedule-${q.id}`}
                      value={rebook[q.id] || ""}
                      onChange={(e) => setRebook((r) => ({ ...r, [q.id]: e.target.value }))}
                      className="h-8 px-2 rounded-lg border border-[#29344D] text-xs min-w-0 max-w-full"
                    />
                    <button data-testid={`reschedule-save-${q.id}`} onClick={() => rescheduleQueue(q)} disabled={busy === q.id || !rebook[q.id]}
                            className="shrink-0 text-xs font-medium text-[#00E5FF] hover:text-[#00B8CC] disabled:opacity-40">Reschedule</button>
                  </div>
                )}
              </div>
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                <button data-testid={`queue-preview-${q.id}`} onClick={() => setQueueOpen(queueOpen === q.id ? "" : q.id)}
                        className="text-xs font-medium text-slate-400 hover:text-[#00E5FF] flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" /> {queueOpen === q.id ? "Hide" : "Preview"}
                </button>
                {["queued", "dry_run", "failed"].includes(q.status) && (
                  <>
                    <button data-testid={`queue-postnow-${q.id}`} disabled={busy === q.id} onClick={() => approveQueue(q, true)}
                            className="h-8 px-3 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                      {busy === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Post now
                    </button>
                    <button data-testid={`queue-approve-${q.id}`} disabled={busy === q.id} onClick={() => approveQueue(q, false)}
                            className="h-8 px-3 rounded-full border border-[#29344D] text-[#00E5FF] hover:border-[#00E5FF]/40 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                      <CalendarDays className="w-3.5 h-3.5" /> Approve{q.scheduled_time ? ` · ${fmtWhen(q.scheduled_time)}` : ""}
                    </button>
                    <button data-testid={`queue-edit-shee-${q.id}`} disabled={busy === q.id} onClick={() => sendToShee(q, "edit")}
                            title="Send this post back to Shee in the chat to revise it"
                            className="h-8 px-3 rounded-full border border-[#29344D] text-slate-400 hover:text-[#00E5FF] hover:border-[#00E5FF]/40 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                      <PenLine className="w-3.5 h-3.5" /> Edit with Shee
                    </button>
                    <button data-testid={`queue-reject-${q.id}`} disabled={busy === q.id} onClick={() => sendToShee(q, "reject")}
                            title="Reject and send back to Shee to rework"
                            className="h-8 px-3 rounded-full border border-[#29344D] text-slate-400 hover:text-red-500 hover:border-red-500/30 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  </>
                )}
                {!["queued", "dry_run", "failed"].includes(q.status) && (
                  <>
                    <button data-testid={`queue-postdirect-${q.id}`} disabled={busy === q.id} onClick={() => repostQueue(q, true)}
                            title="Post straight to your Facebook Page now (bypass PostPulse)"
                            className="h-8 px-3 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                      {busy === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Post directly now
                    </button>
                    <button data-testid={`queue-reopen-${q.id}`} disabled={busy === q.id} onClick={() => reopenQueue(q)}
                            className="h-8 px-3 rounded-full border border-[#29344D] text-[#00E5FF] hover:border-[#00E5FF]/40 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                      <RefreshCw className="w-3.5 h-3.5" /> Re-open
                    </button>
                  </>
                )}
                <button data-testid={`queue-delete-${q.id}`} disabled={busy === q.id} onClick={() => delQueue(q.id)}
                        className="text-xs font-medium text-slate-500 hover:text-red-500 flex items-center gap-1 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            </div>

            {/* Destinations + format — editable while queued */}
            {["queued", "dry_run", "failed"].includes(q.status) && (
              <div data-testid={`queue-destinations-${q.id}`} className="pt-3 border-t border-[#1E273D] flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Destinations</span>
                <button data-testid={`dest-fb-${q.id}`} onClick={() => toggleDest(q, "fb_direct")}
                        className={`h-7 px-3 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-colors ${q.fb_direct ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-500 hover:border-[#00E5FF]/40"}`}>
                  {q.fb_direct && <Check className="w-3 h-3" />} Facebook (Direct)
                </button>
                <button data-testid={`dest-blog-${q.id}`} onClick={() => toggleDest(q, "blog")}
                        className={`h-7 px-3 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-colors ${q.blog ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-500 hover:border-[#00E5FF]/40"}`}>
                  {q.blog && <Check className="w-3 h-3" />} Blog
                </button>
                <span className="text-slate-400">·</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-500">PostPulse</span>
                {PLATFORMS.map((pl) => (
                  <button key={pl.key} data-testid={`dest-pp-${q.id}-${pl.key}`} onClick={() => togglePP(q, pl.key)}
                          className={`h-7 px-2.5 rounded-full border text-xs font-medium transition-colors ${(q.platforms || []).includes(pl.key) ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]" : "border-[#29344D] text-slate-500 hover:border-[#00E5FF]/40"}`}>
                    {pl.label}
                  </button>
                ))}
                <span className="text-slate-400">·</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Format</span>
                <div className="flex gap-1 bg-[#141B29] p-0.5 rounded-full">
                  <button data-testid={`fmt-standard-${q.id}`} onClick={() => setFormat(q, false)}
                          className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${!q.text_only ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-500"}`}>
                    Standard
                  </button>
                  <button data-testid={`fmt-nugget-${q.id}`} onClick={() => setFormat(q, true)}
                          className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${q.text_only ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-500"}`}>
                    Daily Nugget
                  </button>
                </div>
              </div>
            )}
            {queueOpen === q.id && (
              <div data-testid={`queue-preview-panel-${q.id}`} className="mt-3 pt-4 border-t border-[#1E273D] space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Review before posting</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {Object.keys(q.captions || {}).map((plat) => (
                    <div key={plat} className="rounded-lg border border-[#1E273D] p-3 space-y-2" data-testid={`queue-preview-${q.id}-${plat}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-300">{PLATFORM_LABEL[plat] || plat}</span>
                        <span className="text-[10px] text-slate-500">{PLATFORM_SHAPE[plat] || "square"}</span>
                      </div>
                      <img
                        src={`${BACKEND}/api/blog/${q.slug}/post-image.png?shape=${PLATFORM_SHAPE[plat] || "square"}`}
                        alt={q.image_alt || "Post image"}
                        className={`w-full rounded-md border border-[#29344D] object-cover ${q.text_only ? "hidden" : ""}`} />
                      {["queued", "dry_run", "failed"].includes(q.status) ? (
                        <>
                          <textarea
                            data-testid={`queue-caption-edit-${q.id}-${plat}`}
                            value={capEdit[`${q.id}::${plat}`] ?? ((q.captions || {})[plat] || "")}
                            onChange={(e) => setCapEdit((c) => ({ ...c, [`${q.id}::${plat}`]: e.target.value }))}
                            rows={4}
                            className="w-full text-xs text-slate-400 rounded-md border border-[#29344D] bg-[#0B0F16] p-2 leading-relaxed focus:outline-none focus:border-[#00E5FF]/50"
                          />
                          <div className="flex items-center gap-3">
                            <button
                              data-testid={`queue-caption-save-${q.id}-${plat}`}
                              onClick={() => saveCaptionEdit(q, plat)}
                              disabled={capBusy === `${q.id}::${plat}` || capEdit[`${q.id}::${plat}`] == null || capEdit[`${q.id}::${plat}`] === ((q.captions || {})[plat] || "")}
                              className="text-[11px] font-medium text-[#00E5FF] hover:text-[#00B8CC] flex items-center gap-1 disabled:opacity-40"
                            >
                              {capBusy === `${q.id}::${plat}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save caption
                            </button>
                            <button onClick={() => copy((q.captions || {})[plat])} className="text-slate-500 hover:text-[#00E5FF] flex items-center gap-1 text-[11px]">
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-slate-400 whitespace-pre-wrap leading-relaxed">{(q.captions || {})[plat]}</p>
                          <button onClick={() => copy((q.captions || {})[plat])} className="text-slate-500 hover:text-[#00E5FF] flex items-center gap-1 text-[11px]">
                            <Copy className="w-3 h-3" /> Copy caption
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {q.image_alt && <p className="text-[11px] text-slate-500">Image alt: {q.image_alt}</p>}
              </div>
            )}
            {!liveMode && q.status === "dry_run" && (
              <p className="text-[11px] text-slate-500">Dry-run only — posts are validated and staged. Connect your PostPulse key to publish for real.</p>
            )}
          </div>
        ))}
        {view === "queue" && archivedQ.length > 0 && (
          <div className="border border-dashed border-[#29344D] rounded-2xl bg-[#0B0F16]/40" data-testid="queue-archive">
            <button data-testid="queue-archive-toggle" onClick={() => setShowArchive((v) => !v)}
              className="w-full flex items-center gap-3 p-4 text-left">
              <span className="w-8 h-8 rounded-lg bg-[#141B29] text-slate-500 flex items-center justify-center shrink-0"><Archive className="w-4 h-4" strokeWidth={1.7} /></span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-400">Archived · older than 7 days</span>
                <span className="block text-xs text-slate-500">{archivedQ.length} posted — hidden to reduce clutter, still here to reuse</span>
              </span>
              <ChevronRight className={`ml-auto w-4 h-4 text-slate-500 transition-transform ${showArchive ? "rotate-90" : ""}`} />
            </button>
            {showArchive && (
              <div className="border-t border-dashed border-[#29344D] p-2 space-y-1">
                {archivedQ.map((q) => (
                  <div key={q.id} data-testid={`queue-arch-${q.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#0E131F] transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-300 truncate">{q.title}</div>
                      <div className="text-xs text-slate-500 truncate">{(q.platforms || []).join(", ")} · {fmtWhen(q.scheduled_time)}</div>
                    </div>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${Q_STATUS_STYLE[q.status] || ""}`}>{q.status === "dry_run" ? "dry-run" : q.status}</span>
                    <button data-testid={`queue-arch-copy-${q.id}`} onClick={() => copy(Object.values(q.captions || {})[0] || "")}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#29344D] text-slate-400 hover:bg-[#0B0F16] text-xs px-3 py-1.5">
                      <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Reuse
                    </button>
                    <button data-testid={`queue-arch-del-${q.id}`} onClick={() => delQueue(q.id)} disabled={busy === q.id}
                      className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-500 hover:text-red-500 hover:bg-red-500/10" aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>)}
      </section>
      )}

      {/* CONTENT CALENDAR (blog drafts) — collapsed below the social queue by default so the queue's
          platform tabs stay right at the top of the Queue tab, not buried under a long draft list */}
      {view === "queue" && (
      <section className="space-y-3">
        <button data-testid="cal-toggle" onClick={() => setCalOpen((v) => !v)}
                className="w-full flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-400">
          <CalendarDays className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Content calendar
          <ChevronDown className={`ml-auto w-4 h-4 text-slate-500 transition-transform ${calOpen ? "" : "-rotate-90"}`} />
        </button>
        {calOpen && (<div className="space-y-3">
        {loadingPosts && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {!loadingPosts && posts.length === 0 && (
          <p className="text-sm text-slate-500">No blog posts yet. Create one in Chat with Shee or the Manual tab.</p>
        )}
        {posts.filter((p) => matchesSearch(`${p.title || ""} ${p.quote || ""} ${p.topic_angle || ""}`, hubSearch)).map((p) => (
          <div key={p.id} data-testid={`content-post-${p.id}`} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg text-slate-200 truncate">{p.title}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
                  <span className="text-xs text-slate-500">
                    {p.status === "published"
                      ? `published ${(p.published_at || "").slice(0, 10)}`
                      : `scheduled ${p.scheduled_publish_date}`}
                  </span>
                  {p.social_posts && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#10222A] text-[#00E5FF] flex items-center gap-1"><Share2 className="w-3 h-3" /> social ready</span>}
                  {p.source && p.source !== "ai_engine" && (
                    <span data-testid={`content-source-${p.id}`} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 flex items-center gap-1">
                      <UserPlus className="w-3 h-3" /> via {p.source}
                    </span>
                  )}
                </div>
              </div>
              <button data-testid={`post-expand-${p.id}`} onClick={() => setOpenId(openId === p.id ? "" : p.id)} className="text-slate-500 hover:text-[#00E5FF]">
                <ChevronDown className={`w-5 h-5 transition-transform ${openId === p.id ? "rotate-180" : ""}`} />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {p.status !== "scheduled" && p.status !== "published" && (
                <button data-testid={`content-approve-${p.id}`} disabled={busy === p.id}
                        onClick={() => patchPost(p.id, { status: "scheduled" }, "Approved & scheduled")}
                        className="text-xs font-medium text-[#00E5FF] hover:text-[#00B8CC] flex items-center gap-1 disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" /> Approve & schedule
                </button>
              )}
              {p.status !== "published" && (
                <button data-testid={`content-publish-${p.id}`} disabled={busy === p.id}
                        onClick={() => patchPost(p.id, { status: "published" }, "Published")}
                        className="text-xs font-medium text-green-400 hover:text-green-300 flex items-center gap-1 disabled:opacity-50">
                  <Send className="w-3.5 h-3.5" /> Publish now
                </button>
              )}
              <button data-testid={`content-social-${p.id}`} disabled={socialBusy === p.id}
                      onClick={() => genSocial(p)}
                      className="text-xs font-medium text-[#00E5FF] hover:text-[#00B8CC] flex items-center gap-1 disabled:opacity-50">
                {socialBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                {p.social_posts ? "Regenerate social" : "Generate social posts"}
              </button>
              {p.status !== "rejected" && (
                <button data-testid={`content-reject-${p.id}`} disabled={busy === p.id}
                        onClick={() => patchPost(p.id, { status: "rejected" }, "Rejected")}
                        className="text-xs font-medium text-slate-500 hover:text-slate-400 flex items-center gap-1 disabled:opacity-50">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              )}
              <button data-testid={`content-delete-${p.id}`} disabled={busy === p.id}
                      onClick={() => del(p.id)}
                      className="text-xs font-medium text-slate-500 hover:text-red-500 flex items-center gap-1 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>

            {openId === p.id && (
              <div className="mt-4 pt-4 border-t border-[#1E273D] space-y-3">
                <input data-testid={`content-edit-title-${p.id}`} value={ed(p, "title")}
                       onChange={(e) => setEd(p.id, "title", e.target.value)}
                       className="w-full h-10 px-3 rounded-lg border border-[#29344D] text-sm font-medium" />
                <label className="text-xs text-slate-400 flex items-center gap-2">
                  Publish date
                  <input type="date" value={ed(p, "scheduled_publish_date")}
                         onChange={(e) => setEd(p.id, "scheduled_publish_date", e.target.value)}
                         className="h-9 px-2 rounded-lg border border-[#29344D] text-sm" />
                </label>
                <textarea data-testid={`content-edit-body-${p.id}`} value={ed(p, "body_markdown")}
                          onChange={(e) => setEd(p.id, "body_markdown", e.target.value)}
                          rows={12}
                          className="w-full p-3 rounded-lg border border-[#29344D] text-sm font-mono leading-relaxed" />
                <div className="rounded-lg bg-[#07090E] border border-[#1E273D] p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Preview</div>
                  <MarkdownLite text={ed(p, "body_markdown")} />
                </div>
                <button data-testid={`content-save-${p.id}`} onClick={() => saveEdit(p)} disabled={busy === p.id}
                        className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  <PencilLine className="w-3.5 h-3.5" /> Save changes
                </button>

                {/* Social distribution */}
                {p.social_posts && (
                  <div data-testid={`content-social-panel-${p.id}`} className="mt-2 pt-4 border-t border-[#1E273D] space-y-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] flex items-center gap-1"><Share2 className="w-3 h-3" /> Social micro-posts</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {PLATFORMS.map((pl) => p.social_posts[pl.key] && (
                        <div key={pl.key} className="rounded-lg border border-[#1E273D] p-3">
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-medium text-slate-400 flex items-center gap-2">
                              <input type="checkbox"
                                     data-testid={`social-sel-${p.id}-${pl.key}`}
                                     checked={(platformSel[p.id]?.[pl.key]) ?? (pl.key === "facebook")}
                                     onChange={(e) => setPlatformSel((s) => ({ ...s, [p.id]: { ...s[p.id], [pl.key]: e.target.checked } }))} />
                              {pl.label}
                            </label>
                            <button onClick={() => copy(p.social_posts[pl.key])} className="text-slate-500 hover:text-[#00E5FF]"><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="text-xs text-slate-400 whitespace-pre-wrap leading-relaxed">{p.social_posts[pl.key]}</div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 items-start">
                      <div className="rounded-lg border border-[#1E273D] p-2 bg-[#07090E] w-full sm:w-auto">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> {p.has_photo ? "Post photo" : "Quote card"}
                        </div>
                        <img
                          data-testid={`post-image-${p.id}`}
                          src={`${BACKEND}/api/blog/${p.slug}/post-image.png?v=${quoteBust[p.id] || 0}`}
                          alt={p.image_alt || "Post image"} className="w-40 h-40 rounded-md object-cover border border-[#29344D]" />
                        <div className="text-[10px] text-slate-500 mt-1 max-w-40">
                          {p.has_photo
                            ? `Real-life photo · shapes: ${(p.image_shapes || []).join(", ") || "square"}`
                            : "Fell back to the branded quote card"}
                        </div>
                        {/* Prompt peek + reroll */}
                        <textarea
                          data-testid={`image-prompt-${p.id}`}
                          value={imgPrompt[p.id] ?? p.image_prompt ?? ""}
                          onChange={(e) => setImgPrompt((s) => ({ ...s, [p.id]: e.target.value }))}
                          rows={4}
                          placeholder="Describe the real-life scene to photograph…"
                          className="mt-2 w-full sm:w-40 text-[11px] leading-snug rounded-md border border-[#29344D] bg-[#0E131F] p-2 resize-y focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40"
                        />
                        <button
                          data-testid={`regen-image-${p.id}`}
                          onClick={() => regenPhoto(p)}
                          disabled={imgBusy === p.id}
                          className="mt-1 w-full sm:w-40 h-8 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#10222A] text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                        >
                          {imgBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          {imgBusy === p.id ? "Generating…" : "New photo"}
                        </button>
                        <p className="text-[10px] text-slate-500 mt-1 max-w-40">Edit the scene, then reroll — captions stay the same.</p>
                      </div>
                      <div className="flex-1 text-xs text-slate-400">
                        <p className="mb-2"><span className="font-medium text-slate-400">Quote:</span> {p.quote}</p>
                        <label className="block text-[11px] text-slate-400 mb-1">Schedule for (optional — leave empty to post on approval)</label>
                        <input
                          type="datetime-local"
                          data-testid={`schedule-at-${p.id}`}
                          value={scheduleAt[p.id] || ""}
                          onChange={(e) => setScheduleAt((s) => ({ ...s, [p.id]: e.target.value }))}
                          className="h-9 px-2 rounded-lg border border-[#29344D] text-sm mb-2 block"
                        />
                        <button data-testid={`content-queue-${p.id}`} onClick={() => addToQueue(p)} disabled={socialBusy === p.id}
                                className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                          <Send className="w-3.5 h-3.5" /> Add to social queue
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        </div>)}
      </section>
      )}
    </div>
  );
}
